import * as React from "react";
import { useNavigate } from "react-router";

// Zentraler „Zurück"-Mechanismus: statt fest auf eine Route zu navigieren
// (z.B. immer /contacts, egal woher man kam), geht der Button auf die echte
// Routing-History zurück — „immer dahin, wo man vorher war". Nur wenn es keinen
// In-App-Vorgänger gibt (Direkteinstieg / Deep-Link / frischer Tab), wird die
// angegebene Fallback-Route genutzt.
//
// react-router v7 (BrowserRouter) legt in `window.history.state` einen
// monotonen Index `idx` ab (Position im vom Router verwalteten History-Stack).
// `idx > 0` ⇒ es gibt einen In-App-Eintrag hinter uns ⇒ navigate(-1) bleibt in
// der App. Fehlt das Signal (idx nicht gesetzt), gilt bewusst „kein Zurück" →
// Fallback-Route: dann verhält sich der Button exakt wie die bisherige feste
// Navigation. Kein Regressionsrisiko, im Best Case korrekte History.
//
// Bewusst React-arm gehalten: die Entscheidungslogik (canGoBackInApp,
// resolveBackTarget) ist pur und im Vitest-„node"-Runner ohne DOM testbar; der
// Hook ist nur dünner Glue um useNavigate + window.history.state.

/** Liest aus dem react-router-History-State, ob ein In-App-Vorgänger existiert. */
export function canGoBackInApp(historyState: unknown): boolean {
  const idx = (historyState as { idx?: unknown } | null | undefined)?.idx;
  return typeof idx === "number" && idx > 0;
}

export interface BackTargetInput {
  /** Ergebnis von canGoBackInApp(window.history.state). */
  canGoBack: boolean;
  /** Route, die genutzt wird, wenn es keinen In-App-Vorgänger gibt. */
  fallbackTo?: string;
}

export type BackTarget =
  | { kind: "history" }
  | { kind: "fallback"; to: string }
  | { kind: "none" };

/**
 * Entscheidet, wohin „Zurück" führt:
 * - In-App-Vorgänger vorhanden → History-POP (navigate(-1)).
 * - sonst, wenn Fallback definiert → feste Fallback-Route.
 * - sonst → nichts (Aufrufer kann den Button ausblenden/deaktivieren).
 */
export function resolveBackTarget(i: BackTargetInput): BackTarget {
  if (i.canGoBack) return { kind: "history" };
  if (i.fallbackTo) return { kind: "fallback", to: i.fallbackTo };
  return { kind: "none" };
}

export interface SmartBackOptions {
  /** State, der bei Fallback-Navigation mitgegeben wird (z.B. { defaultTab }). */
  state?: unknown;
  /** Bei Fallback-Navigation die aktuelle History-Position ersetzen. */
  replace?: boolean;
}

/**
 * Liefert einen `goBack`-Handler.
 * @param fallbackTo Route für den Deep-Link-Fall (Default: Dashboard "/").
 */
export function useSmartBack(
  fallbackTo: string = "/",
  options?: SmartBackOptions,
): () => void {
  const navigate = useNavigate();
  const state = options?.state;
  const replace = options?.replace;
  return React.useCallback(() => {
    const historyState =
      typeof window !== "undefined" ? window.history.state : null;
    const target = resolveBackTarget({
      canGoBack: canGoBackInApp(historyState),
      fallbackTo,
    });
    if (target.kind === "history") {
      navigate(-1);
    } else if (target.kind === "fallback") {
      navigate(target.to, { state, replace });
    }
  }, [navigate, fallbackTo, state, replace]);
}
